/**
 * Browser-side cache for gallery images (Cache Storage API), so refreshing /photos
 * shows already-seen thumbnails and display images without another Firebase request.
 *
 * Entries are keyed by Storage path plus the photo's upload version, so a re-uploaded
 * photo is fetched fresh. The whole cache is cleared on sign-out and when a guest's
 * 24-hour session ends (see AuthContext), so photos don't linger on shared devices.
 *
 * Every call fails soft: if Cache Storage is unavailable (private browsing, http,
 * quota exceeded) the gallery just fetches from Firebase as normal.
 */
const CACHE_NAME = "gallery-images-v1";

const cacheKey = (path: string, version?: number) =>
  `/__gallery-cache__/${encodeURIComponent(path)}?v=${version ?? 0}`;

const openCache = async (): Promise<Cache | null> => {
  try {
    return typeof caches === "undefined" ? null : await caches.open(CACHE_NAME);
  } catch {
    return null;
  }
};

export async function getCachedImage(path: string, version?: number): Promise<Blob | null> {
  try {
    const cache = await openCache();
    const response = await cache?.match(cacheKey(path, version));
    return response ? await response.blob() : null;
  } catch {
    return null;
  }
}

export async function putCachedImage(path: string, version: number | undefined, blob: Blob): Promise<void> {
  try {
    const cache = await openCache();
    await cache?.put(cacheKey(path, version), new Response(blob, { headers: { "Content-Type": blob.type } }));
  } catch {
    // Quota or availability issues just mean no caching.
  }
}

export async function clearGalleryImageCache(): Promise<void> {
  try {
    if (typeof caches !== "undefined") await caches.delete(CACHE_NAME);
  } catch {
    // Nothing to clear.
  }
}
